import { useLayoutEffect, useRef } from "react";

/**
 * AutoCell — table-cell editor that grows VERTICALLY only.
 *
 * - Uses <textarea rows=1> internally so the browser handles word-wrap.
 * - Resizes its height to match content on every render; never widens.
 * - Cell width is controlled by the parent <td>; long content wraps onto new
 *   lines instead of expanding sideways.
 * - Numeric mode: keeps Enter from inserting newlines + sets inputMode.
 * - The height persists naturally — it's a pure derivation of `value`.
 */
export default function AutoCell({
    value,
    onChange,
    readOnly = false,
    numeric = false,
    className = "",
    style,
    placeholder,
    dir,
    onBlur,
    "data-testid": testId,
}) {
    const ref = useRef(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Reset to auto so shrinking works; then set to content height.
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [value]);

    return (
        <textarea
            ref={ref}
            rows={1}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => {
                if (readOnly) return;
                onChange?.(e.target.value);
            }}
            onBlur={onBlur}
            onKeyDown={(e) => {
                // In numeric cells, treat Enter as commit (no newline).
                if (numeric && e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                }
            }}
            readOnly={readOnly}
            inputMode={numeric ? "decimal" : undefined}
            dir={dir || (numeric ? "ltr" : undefined)}
            data-testid={testId}
            className={
                "block w-full resize-none border-0 bg-transparent outline-none focus-visible:ring-1 focus-visible:ring-amber-300 " +
                (numeric ? "font-numbers text-center " : "text-right ") +
                className
            }
            style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflow: "hidden",
                lineHeight: "1.4",
                minHeight: "2rem",
                padding: "0.375rem 0.5rem",
                ...style,
            }}
        />
    );
}
